import React, { useState } from 'react';
import {
  ClipboardList,
  HardHatIcon,
  User,
  CheckCircle,
  AlertCircle,
  Loader2,
  ArrowRight,
} from 'lucide-react';
import { ROUTES } from '../../constants/routes';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../types/user';
import {
  ApprovalOrder,
  AssignedOrder,
  MoldOrderType,
} from '../../types/mold';
import { useMyTasks } from '../../hooks/useMyTasks';
import { useConnectivity } from '../../hooks/useConnectivity';
import { ExtendedMoldOrderFormConfig } from '../../models/appState';
import { useAppState } from '../../context/AppStateContext';

import { getIconComponent } from '../../utils/icon';
import TaskRow from './TaskRow';
import TabButton from './TabButton';
import { sortMyWork } from '../../utils/myWork';

type TabType = 'all' | 'approval' | string;

interface MyTaskSectionProps {
  user: UserProfile;
  moldOrderGroups: ExtendedMoldOrderFormConfig[];
  showSeeAllButton?: boolean;
  previewLimit?: number | null;
}
const MyTaskSection: React.FC<MyTaskSectionProps> = ({
  user,
  moldOrderGroups,
  showSeeAllButton = false,
  previewLimit = 5,
}) => {
  const navigate = useNavigate();
  const { tasks, approvalTasks, loading, error, refresh } = useMyTasks();
  const { status: connectivityStatus } = useConnectivity();
  const {
    homepage: { taskTabs: taskTabsConfig },
  } = useAppState();

  const [activeType, setActiveType] = useState<TabType>('all');

  // Determine tabs based on configuration mode
  const taskTabs =
    taskTabsConfig.type === 'computed'
      ? moldOrderGroups.map((group) => ({
          id: group.key,
          label: group.title,
          show: true,
          moldOrderTypes: group.moldOrderTypes,
        }))
      : taskTabsConfig.tabs;

  // Filter tasks based on tab configuration
  const getTasksForTab = (tabId: string) => {
    const tabConfig = taskTabs.find((t) => t.id === tabId);
    if (!tabConfig) return [];

    // Use mold order types from the tab (either computed or defined)
    return tasks.filter((task: AssignedOrder) =>
      tabConfig.moldOrderTypes.includes(task.type as MoldOrderType),
    );
  };

  const filteredTasks = sortMyWork(
    activeType === 'all'
      ? [...tasks, ...approvalTasks]
      : activeType === 'approval'
        ? approvalTasks
        : getTasksForTab(activeType),
  );
  const visibleTasks = previewLimit == null
    ? filteredTasks
    : filteredTasks.slice(0, previewLimit);
  const hiddenTaskCount = filteredTasks.length - visibleTasks.length;

  const allCount = tasks.length + approvalTasks.length;

  return (
    <div className='w-full'>
      <div className='bg-white rounded-xl shadow p-2.5 lg:p-4 border border-blue-100'>
        <div
          data-testid='my-work-controls'
          className={previewLimit == null
            ? 'sticky top-0 z-20 -mx-2.5 mb-3 border-b border-gray-200 bg-white px-2.5 pb-2 pt-1 shadow-sm lg:-mx-4 lg:px-4 lg:pt-0'
            : ''}
        >
        <div className='flex items-center gap-3 mb-2'>
          <User className='h-7 w-7 text-blue-600' />
          <div className='flex-1'>
            <div className='text-base font-bold text-gray-900'>
              คุณ{user?.full_name}
            </div>
            <div className='text-xs text-gray-500 flex gap-2 items-center'>
              <span>งานที่ได้รับมอบหมาย</span>
              <span className='text-blue-600 font-bold'>{tasks.length}</span>
              <button
                onClick={() => setActiveType('approval')}
                className='ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold hover:bg-amber-200 flex items-center'
                aria-label='ดูงานที่รออนุมัติ'
              >
                <span className='mr-1 inline-flex items-center'>
                  <CheckCircle className='h-4 w-4' />
                </span>
                รออนุมัติ: {approvalTasks.length}
              </button>
            </div>
          </div>
          {showSeeAllButton && (
            <button
              onClick={() =>
                navigate(`${ROUTES.MY_WORKS}?source=dashboard`)
              }
              className='text-xs px-3 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium'
            >
              ดูทั้งหมด
            </button>
          )}
        </div>
        {/* Filter Button Group - Enhanced UI & Mobile Friendly */}
        <div className='relative md:mb-3'>
          <div className='flex gap-2 overflow-x-auto scrollbar-hide p-2 bg-blue-50/60 rounded-xl shadow-sm whitespace-nowrap -mx-2 sm:mx-0'>
            <TabButton
              id='tab-all'
              label='ทั้งหมด'
              count={allCount}
              Icon={ClipboardList}
              active={activeType === 'all'}
              onClick={() => setActiveType('all')}
              tabIndex={0}
            />
            {/* Approval button */}
            <TabButton
              id='tab-approval'
              label='รออนุมัติ'
              count={approvalTasks.length}
              Icon={CheckCircle}
              active={activeType === 'approval'}
              variant='approval'
              onClick={() => setActiveType('approval')}
              tabIndex={0}
            />
            {taskTabs
              .filter((tab) => tab.show) // Only show visible tabs
              .map((tabConfig, index) => {
                const group = moldOrderGroups.find(
                  (g) => g.key === tabConfig.id,
                );
                const IconComponent = group?.uiConfig
                  ? (getIconComponent(group.uiConfig) ?? HardHatIcon)
                  : HardHatIcon;
                const count = getTasksForTab(tabConfig.id).length;

                return (
                  <TabButton
                    id={`tab-${tabConfig.id}`}
                    key={tabConfig.id}
                    label={tabConfig.label}
                    count={count}
                    Icon={IconComponent}
                    active={activeType === tabConfig.id}
                    onClick={() => setActiveType(tabConfig.id)}
                    tabIndex={index + 1}
                    iconProps={group?.uiConfig?.icon.props}
                  />
                );
              })}
          </div>
          {/* Optional: right fade for scroll hint on mobile */}
          <div className='pointer-events-none absolute top-0 right-0 h-full w-8 rounded-xl bg-gradient-to-l from-blue-50/90 to-transparent' />
        </div>
        </div>

        {/* Distinguish loading / error / offline / empty so a failed or
                    in-flight request never looks like "you have zero work". */}
        {loading ? (
          <div
            className='flex items-center justify-center gap-2 text-gray-500 py-6 text-sm'
            data-testid='my-tasks-loading'
          >
            <Loader2 className='h-4 w-4 animate-spin' aria-hidden='true' />
            <span>กำลังโหลดงาน...</span>
          </div>
        ) : error ? (
          <div
            className='flex flex-col items-center justify-center gap-2 py-6 text-sm'
            data-testid='my-tasks-error'
            role='alert'
          >
            <div className='flex items-center gap-2 text-red-600'>
              <AlertCircle className='h-4 w-4' aria-hidden='true' />
              <span>
                {connectivityStatus === 'offline'
                  ? 'ไม่มีการเชื่อมต่ออินเทอร์เน็ต'
                  : connectivityStatus === 'server-unavailable'
                    ? 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้'
                    : 'เกิดข้อผิดพลาดในการโหลดงาน'}
              </span>
            </div>
            <button
              onClick={refresh}
              className='text-xs px-3 py-1 rounded bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium'
            >
              ลองใหม่
            </button>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className='text-gray-400 text-center py-6 text-sm'>
            ไม่มีงานที่ได้รับมอบหมาย
          </div>
        ) : (
          <ul className={`divide-y divide-blue-50 ${previewLimit == null ? '' : 'lg:max-h-80 lg:overflow-y-auto'}`}>
            {visibleTasks.map((moldOrder, index) => {
              const isApproval =
                activeType === 'approval' ||
                (activeType === 'all' &&
                  moldOrder.id != null &&
                  approvalTasks.some(
                    (a: ApprovalOrder) => a.id === moldOrder.id,
                  ));
              return (
                <TaskRow
                  key={moldOrder.id ?? `task-${index}`}
                  moldOrder={moldOrder}
                  isApproval={isApproval}
                  compact={previewLimit != null}
                />
              );
            })}
          </ul>
        )}
        {hiddenTaskCount > 0 && (
          <button
            type='button'
            onClick={() => navigate(`${ROUTES.MY_WORKS}?source=dashboard`)}
            className='mt-3 flex w-full items-center justify-center gap-2 rounded-lg border-2 border-blue-600 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 shadow-sm transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
            aria-label={`ดูงานของฉันทั้งหมด อีก ${hiddenTaskCount} งาน`}
          >
            <span>ดูงานทั้งหมด — อีก {hiddenTaskCount} งาน</span>
            <ArrowRight className='h-4 w-4' aria-hidden='true' />
          </button>
        )}
      </div>
    </div>
  );
};

export default MyTaskSection;
